import React, { useEffect, useState } from "react";

import { Info } from "lucide-react";

import Drawer from "./Drawer";
import { draftRuntime } from "./runtime";
import {
  clusterText,
  type ClusterScope,
  draftParameterValues,
  draftTier,
  IMPLEMENTATION_LABEL,
  reasonLabel,
  RUNTIME_META,
  type SignalAdminDraft,
  type SignalAdminRegistry,
  type SignalDefinition,
  type SignalParameter,
  type SignalTier,
  TIER_META,
  TIER_ORDER,
} from "./types";

interface SignalDetailDrawerProps {
  signalId: string | null;
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
  onClose: () => void;
  onOpenSignal: (id: string) => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-6">
    <h3 className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-400">
      {title}
    </h3>
    {children}
  </section>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 py-1.5 text-[12.5px]">
    <dt className="text-gray-500">{label}</dt>
    <dd className="m-0 text-gray-900">{children}</dd>
  </div>
);

const Chip: React.FC<{
  children: React.ReactNode;
  tone?: "default" | "off";
  onClick?: () => void;
}> = ({ children, tone = "default", onClick }) => {
  const className = `rounded border px-1.5 py-0.5 text-[11px] ${
    tone === "off"
      ? "border-red-200 bg-red-50 text-red-700 line-through"
      : "border-gray-200 bg-gray-50 text-gray-700"
  } ${onClick ? "hover:border-brand hover:text-brand" : ""}`;
  return onClick ? (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  ) : (
    <span className={className}>{children}</span>
  );
};

const Toggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}> = ({ checked, disabled, label, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
      checked ? "bg-emerald-600" : "bg-gray-300"
    }`}
  >
    <span
      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
        checked ? "left-[18px]" : "left-0.5"
      }`}
    />
  </button>
);

const ParameterEditor: React.FC<{
  parameter: SignalParameter;
  draftValue: number | undefined;
  onStage: (value: number | undefined) => void;
}> = ({ parameter, draftValue, onStage }) => {
  const shown = draftValue ?? parameter.vendor_value ?? parameter.default;
  const [text, setText] = useState(String(shown));
  useEffect(() => setText(String(shown)), [shown]);
  const [low, high] = parameter.range;
  const n = Number(text);
  const invalid =
    text.trim() === "" || !Number.isInteger(n) || n < low || n > high;
  const staged = draftValue !== (parameter.override ?? undefined);

  const commit = () => {
    if (invalid) return;
    // Typing back the value that would apply anyway clears the override
    // rather than pinning it.
    const base = parameter.vendor_value ?? parameter.default;
    onStage(n === base ? undefined : n);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[12.5px] font-semibold text-gray-900">
            {parameter.label}
          </div>
          <div className="mt-0.5 font-mono text-[10.5px] text-gray-400">
            {parameter.id} → {parameter.field}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="text-[12px] font-semibold text-gray-500">
            {parameter.operator}
          </span>
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
              if (e.key === "Enter") commit();
            }}
            inputMode="numeric"
            aria-label={parameter.label}
            aria-invalid={invalid}
            className={`h-8 w-16 rounded-md border px-2 text-right text-[13px] font-bold tabular-nums focus:outline-none focus:ring-2 ${
              invalid
                ? "border-red-400 text-red-700 focus:ring-red-200"
                : "border-gray-300 text-brand-dark focus:border-brand focus:ring-brand/20"
            }`}
          />
          <span className="text-[11.5px] text-gray-500">{parameter.unit}</span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
        <span>Default {parameter.default}</span>
        {parameter.vendor_value != null && (
          <span>Vendor admin {parameter.vendor_value}</span>
        )}
        <span>
          Range {low}–{high}
        </span>
        {parameter.applies === "public_data" && (
          <span className="text-amber-700">
            Takes effect when public data is next processed
          </span>
        )}
        {staged && (
          <span className="font-semibold text-amber-700">Unapplied</span>
        )}
      </div>
      {invalid && (
        <div className="mt-1.5 text-[11px] text-red-600">
          Enter a whole number from {low} to {high}.
        </div>
      )}
      {draftValue !== undefined && (
        <button
          type="button"
          onClick={() => onStage(undefined)}
          className="mt-1.5 text-[11px] font-semibold text-brand hover:underline"
        >
          Reset to {parameter.vendor_value != null ? "vendor value" : "default"}
        </button>
      )}
    </div>
  );
};

/** Which Account Health clusters a rule fires for. Picks clusters, never
 * re-cuts them: the score bands (Triage < 55 …) belong to My Accounts. */
const ClusterScopeEditor: React.FC<{
  scope: ClusterScope;
  labels: Record<string, string>;
  draftValue: string[] | undefined;
  onStage: (value: string[] | undefined) => void;
}> = ({ scope, labels, draftValue, onStage }) => {
  const shown = draftValue ?? scope.default;
  const staged =
    JSON.stringify(draftValue ?? null) !==
    JSON.stringify(scope.override ?? null);
  const toggle = (key: string) => {
    const next = Object.keys(labels).filter(k =>
      k === key ? !shown.includes(k) : shown.includes(k)
    );
    if (!next.length) return; // a rule fires for at least one cluster
    // Picking the default again clears the override rather than pinning it.
    onStage(
      JSON.stringify(next) === JSON.stringify(scope.default) ? undefined : next
    );
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
      <div className="text-[12.5px] font-semibold text-gray-900">
        {scope.label}
      </div>
      <div className="mt-0.5 font-mono text-[10.5px] text-gray-400">
        {scope.id} → {scope.field}
      </div>
      <div
        className="mt-2 flex flex-wrap gap-1.5"
        role="group"
        aria-label={scope.label}
      >
        {Object.entries(labels).map(([key, label]) => {
          const on = shown.includes(key);
          return (
            <button
              key={key}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(key)}
              className={`rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${
                on
                  ? "border-brand bg-brand-faint text-brand-dark"
                  : "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
        <span>Default {clusterText(scope.default, labels)}</span>
        <span>Cluster score bands are set in My Accounts</span>
        {staged && (
          <span className="font-semibold text-amber-700">Unapplied</span>
        )}
      </div>
      {scope.note && (
        <div className="mt-1 text-[11px] text-gray-500">{scope.note}</div>
      )}
      {draftValue !== undefined && (
        <button
          type="button"
          onClick={() => onStage(undefined)}
          className="mt-1.5 text-[11px] font-semibold text-brand hover:underline"
        >
          Reset to default
        </button>
      )}
    </div>
  );
};

/** The tier a signal fires at. Opens on the rule's own tier, the way the
 * cluster pickers open on their default. A rule that escalates by itself
 * gets an "Automatic" option for that default, since fixing it at its base
 * tier switches the escalation off. */
export const TierEditor: React.FC<{
  definition: SignalDefinition;
  parameterValues: Record<string, number | string>;
  draftValue: SignalTier | undefined;
  onStage: (value: SignalTier | undefined) => void;
}> = ({ definition, parameterValues, draftValue, onStage }) => {
  const editable = definition.definition_status === "canonical";
  const dynamic = definition.dynamic_tier;
  const staged = (draftValue ?? null) !== (definition.tier_override ?? null);
  const escalation = dynamic
    ? dynamic.when.replace(/\{\{([\w-]+)\}\}/g, (_m, id: string) =>
        String(parameterValues[id] ?? id)
      )
    : null;
  // A static rule's own tier is its default: picking it again clears the
  // override rather than pinning it (the server stores nothing for it).
  const shown: SignalTier | undefined =
    draftValue ?? (dynamic ? undefined : definition.tier);
  const stage = (value: SignalTier | undefined) =>
    onStage(!dynamic && value === definition.tier ? undefined : value);
  const option = (
    value: SignalTier | undefined,
    label: string,
    className: string
  ) => {
    const on = shown === value;
    return (
      <button
        key={value ?? "automatic"}
        type="button"
        aria-pressed={on}
        disabled={!editable}
        onClick={() => stage(value)}
        className={`rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          on
            ? `border-transparent ring-2 ring-brand ${className}`
            : "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
        }`}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label="Signal tier"
      >
        {dynamic &&
          option(
            undefined,
            `Automatic: ${TIER_META[definition.tier].label} → ${
              TIER_META[dynamic.escalated].label
            }`,
            "bg-white text-gray-900"
          )}
        {TIER_ORDER.map(tier =>
          option(tier, TIER_META[tier].label, TIER_META[tier].className)
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
        <span>
          Default {TIER_META[definition.tier].label}
          {dynamic &&
            ` · escalates to ${TIER_META[dynamic.escalated].label} when ${escalation}`}
        </span>
        {staged && (
          <span className="font-semibold text-amber-700">Unapplied</span>
        )}
      </div>
      {editable && draftValue !== undefined && (
        <button
          type="button"
          onClick={() => onStage(undefined)}
          className="mt-1.5 text-[11px] font-semibold text-brand hover:underline"
        >
          Reset to default
        </button>
      )}
      {dynamic && draftValue !== undefined && (
        <div className="mt-1 text-[11px] text-amber-700">
          A fixed tier replaces the automatic escalation — this signal will
          always fire as {TIER_META[draftValue].label}.
        </div>
      )}
      {!editable && (
        <div className="mt-1 text-[11px] text-gray-500">
          Proposed signals can&apos;t have a tier set until they&apos;re
          promoted.
        </div>
      )}
    </div>
  );
};

/** Close (or reopen) the catalogue's open review item for a signal. Staged
 * like every other change; the note is recorded in Change Audit. */
const ReviewControl: React.FC<{
  definitionId: string;
  review: string;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
}> = ({ definitionId, review, draft, onDraftChange }) => {
  const resolved = draft.review_resolved[definitionId];
  const [note, setNote] = useState("");
  if (review === "Keep") return null;
  if (resolved)
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px]">
        <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">
          Resolved
        </span>
        <span className="text-gray-600">{resolved}</span>
        <button
          type="button"
          onClick={() => {
            const next = { ...draft.review_resolved };
            delete next[definitionId];
            onDraftChange({ ...draft, review_resolved: next });
          }}
          className="font-semibold text-brand hover:underline"
        >
          Reopen
        </button>
      </div>
    );
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold text-gray-500">
          How was it resolved?
        </span>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          maxLength={300}
          placeholder="e.g. Agreed with product to keep the current destination"
          className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-[12.5px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
      </label>
      <button
        type="button"
        disabled={!note.trim()}
        onClick={() =>
          onDraftChange({
            ...draft,
            review_resolved: {
              ...draft.review_resolved,
              [definitionId]: note.trim(),
            },
          })
        }
        className="self-start rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-700 hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
      >
        Mark review resolved
      </button>
    </div>
  );
};

interface SignalSettingsContentProps {
  signalId: string;
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
  onOpenSignal: (id: string) => void;
}

/** A signal's settings — definition, dependencies, parameters, evaluation
 * and delivery, review resolution, CTA — shared by this drawer and the
 * Signal settings tab of Target Registry → Signal actions. */
export const SignalSettingsContent: React.FC<SignalSettingsContentProps> = ({
  signalId,
  registry,
  draft,
  onDraftChange,
  onOpenSignal,
}) => {
  const definition = registry.definitions.find(d => d.id === signalId);
  if (!definition) return null;

  const layerLabel = (id: string) =>
    registry.layers.find(l => l.id === id)?.label ?? id;
  const titleOf = (id: string) =>
    registry.definitions.find(d => d.id === id || d.key === id)?.title;
  const executable = definition.implementation_status === "implemented";
  const classifierRules = registry.classifier.rules.filter(r =>
    r.members.includes(definition.id)
  );
  const controls = draft.rules[definition.id] ?? {
    evaluation: false,
    delivery: false,
  };
  const runtime = draftRuntime(definition, draft);
  const parameters = registry.parameters.filter(p =>
    definition.parameters.includes(p.id)
  );
  // The trigger text is written with default thresholds; name any this
  // draft moves away from, so the definition never contradicts the editor.
  const movedParameters = parameters
    .map(p => ({
      p,
      value: draft.parameters[p.id] ?? p.vendor_value ?? p.default,
    }))
    .filter(({ p, value }) => value !== p.default);
  const clusterScopes = (registry.cluster_scopes ?? []).filter(c =>
    (definition.cluster_scopes ?? []).includes(c.id)
  );
  const clusterLabels = registry.cluster_labels ?? {};
  const movedClusters = clusterScopes
    .map(c => ({ c, value: draft.rule_clusters?.[c.id] ?? c.default }))
    .filter(
      ({ c, value }) => JSON.stringify(value) !== JSON.stringify(c.default)
    );

  const stageControl = (control: "evaluation" | "delivery", next: boolean) =>
    onDraftChange({
      ...draft,
      rules: {
        ...draft.rules,
        [definition.id]: { ...controls, [control]: next },
      },
    });

  const stageClusters = (id: string, value: string[] | undefined) => {
    const next = { ...(draft.rule_clusters ?? {}) };
    if (value === undefined) delete next[id];
    else next[id] = value;
    onDraftChange({ ...draft, rule_clusters: next });
  };

  const stageTier = (value: SignalTier | undefined) => {
    const next = { ...(draft.tiers ?? {}) };
    if (value === undefined) delete next[definition.id];
    else next[definition.id] = value;
    onDraftChange({ ...draft, tiers: next });
  };

  const stageParameter = (id: string, value: number | undefined) => {
    const nextParams = { ...draft.parameters };
    if (value === undefined) delete nextParams[id];
    else nextParams[id] = value;
    onDraftChange({ ...draft, parameters: nextParams });
  };

  const dependencyChip = (id: string) =>
    titleOf(id) ? (
      <Chip key={id} onClick={() => onOpenSignal(id)}>
        {titleOf(id)}
      </Chip>
    ) : (
      <Chip key={id}>{id}</Chip>
    );

  return (
    <>
      <Section title="Controls">
        {!executable && (
          <div className="mb-3 flex gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              {definition.implementation_status === "proposed_unimplemented"
                ? "Proposed — not promoted to the canonical registry, so it can't run yet."
                : "Canonical, but ExecBridge doesn't implement this rule yet."}
            </span>
          </div>
        )}
        <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between gap-4 px-3 py-2.5">
            <div>
              <div className="text-[12.5px] font-semibold text-gray-900">
                Rule evaluation
              </div>
              <div className="text-[11.5px] text-gray-500">
                Off: not evaluated. Open signals are left as they are and hidden
                from users.
              </div>
            </div>
            <Toggle
              label="Rule evaluation"
              checked={controls.evaluation}
              disabled={!executable}
              onChange={next => stageControl("evaluation", next)}
            />
          </div>
          <div className="flex items-center justify-between gap-4 px-3 py-2.5">
            <div>
              <div className="text-[12.5px] font-semibold text-gray-900">
                User delivery
              </div>
              <div className="text-[11.5px] text-gray-500">
                Off: still evaluated and recorded, never shown to users.
              </div>
            </div>
            <Toggle
              label="User delivery"
              checked={controls.delivery}
              disabled={!executable}
              onChange={next => stageControl("delivery", next)}
            />
          </div>
        </div>
        <p className="mt-2 text-[11.5px] text-gray-500">
          {RUNTIME_META[runtime].description}
          {definition.state.reasons.length > 0 && (
            <>
              {" "}
              Currently:{" "}
              {definition.state.reasons
                .map(r => reasonLabel(r, layerLabel))
                .join(" · ")}
              .
            </>
          )}
        </p>
        <p className="mt-1 text-[11.5px] text-gray-500">
          {definition.open_firings === null
            ? "Choose an organization in Overview to see its open signals."
            : definition.open_firings > 0
              ? `${definition.open_firings} open in the selected organization.`
              : "None open in the selected organization."}
        </p>
      </Section>

      <Section title="Definition">
        <dl className="m-0">
          <Row label="Trigger">
            {definition.trigger}
            {movedParameters.length + movedClusters.length > 0 && (
              <div className="mt-1 text-[11.5px] text-amber-700">
                Written with default values. Now:{" "}
                {[
                  ...movedParameters.map(
                    ({ p, value }) =>
                      `${p.label} ${p.operator} ${value}${p.unit ? ` ${p.unit}` : ""}`
                  ),
                  ...movedClusters.map(
                    ({ c, value }) =>
                      `${c.label} ${clusterText(value, clusterLabels)}`
                  ),
                ].join(" · ")}
              </div>
            )}
          </Row>
          <Row label="Family">
            {registry.families[definition.family] ?? definition.family} ·{" "}
            {definition.type}
          </Row>
          <Row label="Scope">{definition.scope}</Row>
          <Row label="Personas">{definition.personas.join(" · ")}</Row>
          <Row label="Resolves when">{definition.resolution}</Row>
          <Row label="Review">
            {definition.review}
            {definition.review_note && (
              <span className="text-gray-500"> — {definition.review_note}</span>
            )}
            <ReviewControl
              definitionId={definition.id}
              review={definition.review}
              draft={draft}
              onDraftChange={onDraftChange}
            />
          </Row>
        </dl>
      </Section>

      <Section title="Tier">
        <TierEditor
          definition={definition}
          parameterValues={draftParameterValues(registry, draft)}
          draftValue={draft.tiers?.[definition.id]}
          onStage={stageTier}
        />
        <p className="mt-2 text-[11.5px] text-gray-500">
          Sets the card&apos;s tier on My Accounts — its colour, its group and
          its sort order. Critical cards can&apos;t be snoozed. Applying a
          change re-checks every account; open cards move within a few minutes.
        </p>
      </Section>

      {parameters.length + clusterScopes.length > 0 && (
        <Section title="Parameters">
          <div className="flex flex-col gap-2">
            {clusterScopes.map(c => (
              <ClusterScopeEditor
                key={c.id}
                scope={c}
                labels={clusterLabels}
                draftValue={draft.rule_clusters?.[c.id]}
                onStage={value => stageClusters(c.id, value)}
              />
            ))}
            {parameters.map(p => (
              <ParameterEditor
                key={p.id}
                parameter={p}
                draftValue={draft.parameters[p.id]}
                onStage={value => stageParameter(p.id, value)}
              />
            ))}
          </div>
          <p className="mt-2 text-[11.5px] text-gray-500">
            Parameters are shared by every signal that uses them. Applying a
            change re-checks every account; cards update within a few minutes.
          </p>
        </Section>
      )}

      <Section title="Dependencies">
        <dl className="m-0">
          <Row label="Needs data">
            <div className="flex flex-wrap gap-1">
              {definition.layers.map(l => (
                <Chip
                  key={l}
                  tone={draft.layers[l] === false ? "off" : "default"}
                >
                  {layerLabel(l)}
                </Chip>
              ))}
            </div>
          </Row>
          <Row label="Depends on">
            <div className="flex flex-wrap gap-1">
              {definition.depends.length
                ? definition.depends.map(dependencyChip)
                : "—"}
            </div>
          </Row>
          <Row label="Used by">
            <div className="flex flex-wrap gap-1">
              {definition.used_by.length
                ? definition.used_by.map(dependencyChip)
                : "—"}
            </div>
          </Row>
          <Row label="Rules">
            <div className="flex flex-wrap gap-1">
              {classifierRules.length
                ? classifierRules.map(r => (
                    <span
                      key={r.id}
                      title={r.note}
                      className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-700"
                    >
                      {r.id} · {registry.classifier.types[r.type]} ·{" "}
                      {registry.classifier.statuses[r.status]}
                    </span>
                  ))
                : "—"}
            </div>
          </Row>
          <Row label="Fallback">
            {definition.fallback ? dependencyChip(definition.fallback) : "—"}
          </Row>
        </dl>
        <p className="mt-1 text-[11.5px] text-gray-500">
          Which other signals this one hides, or is hidden by, is set in Rule
          Classifier (applied rules shown). Fallback is recorded from the spec.
        </p>
      </Section>

      <Section title="Delivery">
        <dl className="m-0">
          <Row label="Users see">
            {definition.cta_effective.label}
            <span className="text-gray-500">
              {" "}
              ·{" "}
              {definition.cta_effective.source === "override"
                ? "Izba-admin override"
                : `ExecBridge default → ${definition.cta_default.destination}`}
            </span>
          </Row>
          <Row label="Registry suggests">
            {definition.cta}
            <span className="text-gray-500"> · {definition.destination}</span>
          </Row>
          <Row label="Action target">
            {registry.targets[definition.target] ?? definition.target}
          </Row>
        </dl>
      </Section>
    </>
  );
};

const SignalDetailDrawer: React.FC<SignalDetailDrawerProps> = ({
  signalId,
  registry,
  draft,
  onDraftChange,
  onClose,
  onOpenSignal,
}) => {
  const definition = registry.definitions.find(d => d.id === signalId);
  if (!definition || !signalId) return null;
  const runtime = draftRuntime(definition, draft);
  const tier = draftTier(definition, draft);

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={
        <div className="flex flex-wrap gap-1.5">
          <span
            className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold ${TIER_META[tier].className}`}
          >
            {TIER_META[tier].label}
          </span>
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10.5px] font-bold text-gray-600">
            {IMPLEMENTATION_LABEL[definition.implementation_status]}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 ring-inset ${RUNTIME_META[runtime].className}`}
          >
            {RUNTIME_META[runtime].label}
          </span>
        </div>
      }
      title={definition.title}
      subtitle={definition.id}
    >
      <SignalSettingsContent
        signalId={signalId}
        registry={registry}
        draft={draft}
        onDraftChange={onDraftChange}
        onOpenSignal={onOpenSignal}
      />
    </Drawer>
  );
};

export default SignalDetailDrawer;
